/**
 * LayoutAnimation - I report whether one named group is mid-animation, so that a measure can sit it
 * out. ONLY concerned with that group's transition events - no measuring, no layout, no timers.
 *
 * A group that animates layout makes every `ResizeObserver` on the page fire on each frame, and any
 * that measures or writes from its callback pays for all of them. The transition's own events are
 * the signal rather than a timer, which would put a second copy of the duration in JS.
 *
 * **The group names the one property it is to be judged by.** `transitionrun` and `transitionend`
 * fire per property, so a group easing several would otherwise clear the flag on whichever finished
 * first - the right answer is the property whose duration defines the animation. `ResizableColumns`
 * names `grid-template-columns`, which is the track list it eases; an accordion would name
 * `grid-template-rows`.
 *
 * **One instance per group, because the answer is per group.** A single shared flag pauses a
 * measurement that only cared about the other group, settles subscribers who were not watching this
 * one, and - the one that is a bug rather than waste - ends the pause early: with two groups
 * animating at once, the first to finish clears a flag the second still needs, so the measurements
 * run during exactly the animation this exists to sit out.
 *
 * Consumers are hooks in arbitrary places - inside a column, and outside the group entirely for a
 * header whose height the group's own width changes - so they reach their instance by the group's
 * `name` through the registry below rather than through a context they may be nowhere near.
 */
export class LayoutAnimation {
  /**
   * One instance per `name`, held for the page's life and deliberately never dropped. A consumer may
   * hold nothing but `isAnimating`, which is not a subscription we could find it by, so replacing an
   * instance would leave that consumer reading a dead one and measuring straight through the next
   * animation. Names are written in code rather than derived from data, so what accumulates is one
   * small object per group the code can render.
   */
  static instances = new Map<string, LayoutAnimation>();

  /**
   * The answer for a group with no `name`. Nothing can register against it, so it never animates and
   * never settles - the safe reading of "I cannot tell which group you mean". Shared, because there
   * is nothing to tell two nameless groups apart by.
   */
  static unnamed = new LayoutAnimation();

  animating = false;

  element: HTMLElement | null = null;

  /** The one property this group is judged by, set when its element arrives. */
  property: string | null = null;

  settledCallbacks = new Set<() => void>();

  /**
   * The transition is declared on the group's root, so the group's root is the event's target.
   * `===` and not `contains`: transition events bubble, so a column animating something of its own
   * arrives here too, and the target is the only thing that tells the two apart.
   */
  isOwnTransition = ({ event }: { event: TransitionEvent }) =>
    event.propertyName === this.property && event.target === this.element;

  handleStart = (event: TransitionEvent) => {
    if (this.isOwnTransition({ event })) this.animating = true;
  };

  /**
   * `transitioncancel` ends an animation as much as `transitionend`: a reversal emits cancel then a
   * fresh run, and without it widths reversed mid-animation would never settle.
   */
  handleFinish = (event: TransitionEvent) => {
    if (!this.isOwnTransition({ event })) return;

    this.animating = false;

    /*
      Iterating live is safe for a callback that unsubscribes itself - a deleted entry is simply
      not visited. Adding during the loop is what a `Set` iteration cannot survive: new entries
      are visited, so a callback that subscribed another would loop.
    */
    for (const callback of this.settledCallbacks) callback();
  };

  /**
   * On the group's own root rather than on `document`: a document-wide listener is handed every
   * transition on the page to filter, and there is no reason for one when the transitions that can
   * matter all happen inside this subtree. Attaching a second element detaches the first, so a
   * group that re-registers cannot leave a listener behind on the element it has left.
   */
  attach = ({
    element,
    property,
  }: {
    element: HTMLElement;
    property: string;
  }) => {
    this.detach();

    this.element = element;
    this.property = property;
    element.addEventListener('transitionrun', this.handleStart);
    element.addEventListener('transitionend', this.handleFinish);
    element.addEventListener('transitioncancel', this.handleFinish);
  };

  /**
   * Subscribers are kept rather than dropped: they outlive the group - a header measured outside it,
   * a group remounting under the same name - and re-attaching is what puts them back in service.
   */
  detach = () => {
    const { element } = this;

    if (!element) return;

    /*
      Clearing the flag is part of detaching: the `transitionend` that would have cleared it is no
      longer heard, so a detach mid-animation would otherwise strand it true for the page's life.
    */
    this.animating = false;
    this.element = null;
    this.property = null;
    element.removeEventListener('transitionrun', this.handleStart);
    element.removeEventListener('transitionend', this.handleFinish);
    element.removeEventListener('transitioncancel', this.handleFinish);
  };

  /**
   * Not React state on purpose: a state flag would re-render every consumer twice per animation,
   * which is a smaller version of the problem this exists to solve.
   *
   * A bound property rather than a method, so it can be handed to a hook's `isPaused` as it stands -
   * `isPaused: animation.isAnimating` - with no wrapper to carry `this`.
   */
  isAnimating = () => this.animating;

  /**
   * Returns the unsubscribe. Call it from the same effect that owns the measure and unsubscribe in
   * that effect's cleanup, so an unmounted hook is not measuring a component that has gone.
   *
   * Bound for the same reason as `isAnimating`.
   */
  onSettled = (callback: () => void) => {
    this.settledCallbacks.add(callback);

    return () => {
      this.settledCallbacks.delete(callback);
    };
  };

  /**
   * The instance for a group, whether or not that group has mounted yet - it is created here and
   * the group attaches its element to it later, which is what keeps a subscription made this early
   * alive. Asking first is the ordinary case rather than an edge one: a consumer inside a column
   * subscribes from its own effect, and a child's effect runs before its parent's.
   *
   * Until an element arrives the instance reports no animation and settles nobody, so a consumer
   * needs no special case for a group that has not mounted.
   */
  static for({ name }: { name?: string }) {
    if (!name) return LayoutAnimation.unnamed;

    const existing = LayoutAnimation.instances.get(name);

    if (existing) return existing;

    const created = new LayoutAnimation();

    LayoutAnimation.instances.set(name, created);

    return created;
  }

  /** Called by the group on mount. A group with no `name` cannot be reached, so it is not. */
  static register({
    element,
    name,
    property,
  }: {
    element: HTMLElement;
    name?: string;
    property: string;
  }) {
    if (!name) return;

    LayoutAnimation.for({ name }).attach({ element, property });
  }

  static unregister({ name }: { name?: string }) {
    if (!name) return;

    LayoutAnimation.instances.get(name)?.detach();
  }
}
